import { styled, useTheme } from '@vebox/etui-system';
import { useState, useRef, useMemo, useEffect, createElement } from 'react';
import { ellipsis } from './utilities/ellipsis.js';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from './_rollupPluginBabelHelpers-3b7bd988.js';
import { Div, Span } from '@vebox/etui-system-elements';
import Icon from './Icon/Icon.js';
import { icons } from './Icon/icons.js';
import ClickOutside from './ClickOutside/ClickOutside.js';
import Button from './Button/Button.js';
import { usePopper } from 'react-popper';
import { T as TreeView } from './TreeView-4d6ddaec.js';
import { addParentToChildren } from './TreeviewSelect/utils/addParentToChildren.js';
import { defineOpenSections } from './TreeviewSelect/utils/defineOpenSections.js';
import { flattenData } from './TreeviewSelect/utils/flattenData.js';

const TreeviewSelectContent = styled(Div)`
  display: flex;
  flex-direction: column;
  padding: ${({
  theme
}) => `${theme.spacing.xxs}px ${theme.spacing.xs}px`};
`;

const TreeviewSelectMain = styled(Div)`
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  height: 100%;
  justify-content: center;
  user-select: none;

  button:hover {
    background-color: initial;
    color: ${({
  theme
}) => theme.palette.common.white};
  }
`;

const _excluded = ["theme"];
const TreeviewSelectOptions = styled(Div).attrs(_ref => {
  let {
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2({
    boxShadow: theme.shadows[3],
    p: `${theme.spacing.xxs}px 0`
  }, props);
})`
  display: ${({
  isVisible
}) => isVisible ? 'flex' : 'none'};
  flex-direction: column;
  min-width: 180px;
  overflow-y: auto;
  width: 100%;

  .treeview-group {
    border-bottom: 1px solid ${({
  theme
}) => theme.palette.grey[100]};
  }
`;

const TreeviewSelectWrapper = styled(Div)`
  position: relative;
`;

let DIRECTION = /*#__PURE__*/function (DIRECTION) {
  DIRECTION["PRIOR"] = "prior";
  DIRECTION["NEXT"] = "next";
  return DIRECTION;
}({});

function TreeviewSelect({
  data = [],
  displayKey = 'name',
  preselectedItem = null,
  customStyles = {},
  closeOnSelect = false,
  label = null,
  rowTemplate = null,
  onSelectItem = () => {},
  flatMode = false,
  emptyPlaceholder,
  iconProps = {}
}) {
  const theme = useTheme();
  const [visible, setVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(preselectedItem);
  const [openSections, setOpenSections] = useState([]);
  const referenceRef = useRef(null);
  const popperRef = useRef(null);
  const processedData = useMemo(() => addParentToChildren(data), [data]);
  const processedOptions = useMemo(() => flattenData(processedData), [processedData]);
  useEffect(() => {
    const preselected = processedOptions.find(opt => opt.id === (preselectedItem === null || preselectedItem === void 0 ? void 0 : preselectedItem.id)) || processedData[0];
    setSelectedItem(preselected);
    setOpenSections(defineOpenSections(preselected));
  }, [processedData, processedData.length, preselectedItem, processedOptions]);
  const {
    styles,
    attributes
  } = usePopper(referenceRef.current, popperRef.current, {
    placement: 'bottom-start',
    strategy: 'absolute',
    modifiers: [{
      name: 'computeStyles',
      options: {
        adaptive: false // true by default
      }
    }, {
      name: 'offset',
      enabled: true,
      options: {
        offset: [0, -5]
      }
    }]
  });
  const hide = () => setVisible(false);
  const handleDropdownClick = () => setVisible(prevValue => !prevValue);
  const handleArrowClick = event => {
    hide();
    event.stopPropagation();
    let {
      target
    } = event;
    while (!target.dataset.direction) {
      target = target.parentElement;
    }
    const {
      direction
    } = target.dataset;
    const isNext = direction === DIRECTION.NEXT;
    const indexOfSelectedItem = findSelectedItemIndex(processedOptions);
    let nextItemToSet = processedOptions[isNext ? flatMode && indexOfSelectedItem === processedOptions.length - 2 ? indexOfSelectedItem + 2 : indexOfSelectedItem + 1 : indexOfSelectedItem - 1];
    if (!nextItemToSet) {
      nextItemToSet = isNext ? processedOptions[0] : processedOptions[flatMode ? processedOptions.length - 2 : processedOptions.length - 1];
    }
    onSelectItem(nextItemToSet);
    setSelectedItem(nextItemToSet);
  };
  const handleOpenSectionChange = ids => {
    setOpenSections(ids);
  };
  const findSelectedItemIndex = items => {
    const indexOfSelectedItem = items.findIndex(item => item.id === selectedItem.id);
    return indexOfSelectedItem;
  };
  const onRowSelect = item => {
    setSelectedItem(item);
    onSelectItem(item);
    closeOnSelect && hide();
  };
  return /*#__PURE__*/createElement(ClickOutside, {
    onClickOutside: hide
  }, /*#__PURE__*/createElement(TreeviewSelectWrapper, {
    style: customStyles.wrapper
  }, /*#__PURE__*/createElement(TreeviewSelectMain, {
    onClick: handleDropdownClick,
    style: customStyles.main
  }, /*#__PURE__*/createElement(Button, {
    cursor: "pointer",
    variant: "tertiary",
    testId: "prev-arrow",
    "data-direction": DIRECTION.PRIOR,
    onClick: handleArrowClick,
    style: customStyles.arrows
  }, /*#__PURE__*/createElement(Icon, _extends({
    "data-direction": DIRECTION.PRIOR,
    glyph: icons.CHEVRON_DOWN,
    rotate: 90,
    size: 16,
    styles: customStyles.icons
  }, iconProps))), /*#__PURE__*/createElement(TreeviewSelectContent, {
    style: customStyles.selectContent
  }, label && /*#__PURE__*/createElement(Span, {
    fontSize: "10px",
    color: "#BFBEBE"
  }, label), /*#__PURE__*/createElement(Div, _extends({
    fontSize: "14px"
  }, ellipsis(), {
    title: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem[displayKey]
  }), selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem[displayKey])), /*#__PURE__*/createElement(Button, {
    cursor: "pointer",
    variant: "tertiary",
    testId: "next-arrow",
    "data-direction": DIRECTION.NEXT,
    onClick: handleArrowClick,
    style: customStyles.arrows
  }, /*#__PURE__*/createElement(Icon, _extends({
    "data-direction": DIRECTION.NEXT,
    glyph: icons.CHEVRON_DOWN,
    rotate: -90,
    size: 16,
    styles: customStyles.icons
  }, iconProps)))), /*#__PURE__*/createElement("div", _extends({
    ref: popperRef,
    style: _objectSpread2(_objectSpread2({}, styles.popper), {}, {
      width: '100%',
      zIndex: 1
    }, customStyles.options)
  }, attributes.popper), /*#__PURE__*/createElement(TreeviewSelectOptions, {
    style: styles.offset,
    isVisible: visible
  }, /*#__PURE__*/createElement(TreeView, {
    data: data,
    onRowSelect: onRowSelect,
    rowTemplate: rowTemplate,
    selectedRow: selectedItem,
    onOpenSectionChange: handleOpenSectionChange,
    openSections: openSections,
    emptyPlaceholder: emptyPlaceholder,
    styles: {
      root: {
        p: 0
      },
      row: {
        root: {
          fontWeight: theme.typography.fontWeightBold
        }
      }
    }
  })))));
}

export { DIRECTION as D, TreeviewSelect as T };
